/**
 * useSettingsSync - Sync user settings with database
 *
 * ISSUE_167: readThroughCache 제거 — 순수 Supabase 직조회(서버 권위).
 *
 * This hook:
 * 1. Loads settings from Supabase
 * 2. Applies settings to useGlobalStore (낙관 소스)
 * 3. Syncs settings changes back to DB (debounced)
 *
 * @module useSettingsSync
 */

import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '~/core/store/useAuthStore';
import { useGlobalStore } from '~/core/store/useGlobalStore';
import {
  fetchUserSettings,
  updateUserSettings,
  type UserSettings,
} from '~/services/user/userSettingsService';
import { VALID_MODULE_IDS, REQUIRED_MODULES } from '~/core/config/modules';
import type { AppModule } from '~/core/store/useGlobalStore';

// Debounce delay for saving settings (ms)
const SAVE_DEBOUNCE_MS = 1000;

/**
 * Apply settings to the global store
 */
function applySettings(settings: UserSettings) {
  const store = useGlobalStore.getState();

  if (settings.theme && settings.theme !== store.theme) {
    store.setTheme(settings.theme as "light" | "dark" | "system");
  }
  if (settings.isDarkMode !== undefined && settings.isDarkMode !== store.isDarkMode) {
    store.setDarkMode(settings.isDarkMode);
  }
  if (settings.designStyle && settings.designStyle !== store.designStyle) {
    store.setDesignStyle(settings.designStyle as 'soft' | 'brutal');
  }
  if (settings.activeCustomThemeId !== undefined && settings.activeCustomThemeId !== store.activeCustomThemeId) {
    store.setActiveCustomThemeId(settings.activeCustomThemeId);
  }

  // pinnedModules: 서버에 값이 있을 때만 적용 (undefined → 로컬 유지)
  if (Array.isArray(settings.pinnedModules) && settings.pinnedModules.length > 0) {
    const validated = settings.pinnedModules.filter(
      (id): id is AppModule => VALID_MODULE_IDS.includes(id as AppModule)
    );
    for (const req of REQUIRED_MODULES) {
      if (!validated.includes(req)) validated.unshift(req);
    }
    if (JSON.stringify(validated) !== JSON.stringify(store.pinnedModules)) {
      store.reorderPinnedModules(validated);
    }
  }
}

/**
 * Hook to sync settings between Zustand store and database
 */
export function useSettingsSync() {
  const profile = useAuthStore((state) => state.profile);
  // Track loading state with useState for proper re-render
  const [loadedUserId, setLoadedUserId] = useState<string | null>(null);

  // Refs for non-reactive values
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isApplyingFromDbRef = useRef(false); // Prevent save during load

  // Load settings when profile changes
  useEffect(() => {
    // Skip if no profile
    if (!profile?.id) {
      setLoadedUserId(null);
      return;
    }

    // Skip if already loaded for this user
    if (loadedUserId === profile.id) return;

    const loadSettings = async () => {
      // Mark that we're applying from DB (skip save)
      isApplyingFromDbRef.current = true;

      // ISSUE_167: 순수 Supabase 직조회 (서버 권위)
      try {
        const settings = await fetchUserSettings(profile.id);
        if (settings) {
          applySettings(settings);
        }
      } finally {
        // applySettings로 인한 구독 트리거가 가라앉은 뒤 플래그 해제
        setTimeout(() => {
          isApplyingFromDbRef.current = false;
        }, 100);
      }

      setLoadedUserId(profile.id);
    };

    loadSettings();
  }, [profile?.id, loadedUserId]);

  // Subscribe to settings changes and sync to DB
  useEffect(() => {
    // Skip if no profile
    if (!profile?.id) return;

    // Wait until settings are loaded before subscribing to changes
    if (loadedUserId !== profile.id) return;

    const userId = profile.id;

    // Subscribe to relevant state changes
    const unsubscribe = useGlobalStore.subscribe(
      (state, prevState) => {
        // Skip if we're currently applying settings from DB
        if (isApplyingFromDbRef.current) return;

        // Check if any synced settings changed
        const settingsChanged =
          state.theme !== prevState.theme ||
          state.isDarkMode !== prevState.isDarkMode ||
          state.designStyle !== prevState.designStyle ||
          state.activeCustomThemeId !== prevState.activeCustomThemeId ||
          state.pinnedModules !== prevState.pinnedModules;

        if (!settingsChanged) return;

        // Debounce the save
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
          const currentState = useGlobalStore.getState();
          const settingsToSave: UserSettings = {
            theme: currentState.theme,
            isDarkMode: currentState.isDarkMode,
            designStyle: currentState.designStyle,
            activeCustomThemeId: currentState.activeCustomThemeId,
            pinnedModules: currentState.pinnedModules,
          };

          // ISSUE_167: 순수 Supabase 저장 (캐시 무효화 불필요)
          updateUserSettings(userId, settingsToSave);
        }, SAVE_DEBOUNCE_MS);
      }
    );

    return () => {
      unsubscribe();
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [profile?.id, loadedUserId]);
}
